import gql from "graphql-tag";
import className from "classnames/bind";
import { WifiFormFragment } from "@graphqlTypes";
import { animate } from "motion";
import { useState, useRef, useEffect } from "react";
import { Button, Container, SectionHeader } from "@components";
import CheckYes from "@/assets/svg/check-yes.svg";

import styles from "./WifiForm.module.scss";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { useRouter } from "next/router";
const cx = className.bind(styles);

export default function WifiForm({
  headline,
  subhead,
  body,
  form,
}: WifiFormFragment) {
  const { labels } = usePageWrapperData();
  const router = useRouter();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });

  const [requestStatus, setRequestStatus] = useState<{
    loading: boolean;
    success: boolean | null;
    message?: string;
    sub?: string;
    error?: string;
  }>({
    loading: false,
    success: null,
  });

  const handleRedirect = () => {
    setTimeout(() => {
      const grantUrl = localStorage.getItem('base_grant_url');
      let continueUrl = localStorage.getItem('user_continue_url');
      if (!continueUrl) continueUrl = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/`;
      if (grantUrl) {
        window.top.location.href = `${grantUrl}?continue_url=${encodeURIComponent(continueUrl)}`;
      } else {
        router.push('/');
      }
    }, 1000);
  }

  const onFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    setRequestStatus({ loading: true, success: null });

    try {
      const response = await fetch("/api/wifi/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) {
        setRequestStatus({
          loading: false,
          success: true,
          message: form?.success?.headline,
          sub: form?.success?.subhead,
        });
        setFormData({ first_name: "", last_name: "", email: "" });
        handleRedirect();
      } else {
        setRequestStatus({
          loading: false,
          success: false,
          message: form?.error
        });
      }
    } catch (error) {
      setRequestStatus({
        loading: false,
        success: false,
        message: form?.error,
      });
    } finally {
      setRequestStatus((prev) => ({
        ...prev,
        loading: false,
      }));
      // Reset form fields after submission
      if (formElement) formElement.reset();
    }
  };
  const onFormInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // fade in animation for form and success card
  const formRef = useRef<HTMLFormElement | null>(null);
  const successCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (formRef.current) {
      animate(formRef.current, { opacity: 1 }, { duration: 0.2 });
    }
  }, [formRef.current]);

  useEffect(() => {
    if (successCardRef.current) {
      animate(successCardRef.current, { opacity: 1 }, { duration: 0.2 });
    }
  }, [successCardRef.current, requestStatus]);

  return (
    <section className={cx("wifi-form")}>
      <Container>
        {headline && <SectionHeader headline={headline} />}
        <div className={cx("content")}>
          <div className={cx("intro")}>
            {subhead && (
              <h3 className={cx("subhead", "title-lc-m")}>{subhead}</h3>
            )}
            {body && (
              <div
                className={cx("body", "text-l", "rich-text")}
                dangerouslySetInnerHTML={{ __html: body }}
              />
            )}
          </div>
          <div className={cx("form-container")}>
            {requestStatus.success ? (
              <div className={cx("success-card")} ref={successCardRef}>
                <CheckYes className={cx("icon")} />
                <div className={cx("success-card__message")}>
                  <h4 className={cx("text-l", "text-center")}>
                    {requestStatus.message}
                  </h4>
                  <p className={cx("text-m", "text-center")}>
                    {requestStatus.sub}
                  </p>
                </div>
              </div>
            ) : (
              <form
                className={cx("form")}
                onSubmit={onFormSubmit}
                ref={formRef}
              >
                <div className={cx("input-wrapper")}>
                  <label className={cx("label", "text-m")} htmlFor="first_name">
                    {form.firstName}
                  </label>
                  <input
                    className={cx("input", "text-m")}
                    id="first_name"
                    maxLength={40}
                    name="first_name"
                    required
                    size={20}
                    type="text"
                    value={formData.first_name}
                    onChange={onFormInputChange}
                  />
                </div>

                <div className={cx("input-wrapper")}>
                  <label className={cx("label", "text-m")} htmlFor="last_name">
                    {form.lastName}
                  </label>
                  <input
                    className={cx("input", "text-m")}
                    id="last_name"
                    maxLength={80}
                    name="last_name"
                    required
                    size={20}
                    type="text"
                    value={formData.last_name}
                    onChange={onFormInputChange}
                  />
                </div>

                <div className={cx("input-wrapper")}>
                  <label className={cx("label", "text-m")} htmlFor="email">
                    {form.email}
                  </label>
                  <input
                    className={cx("input", "text-m")}
                    id="email"
                    maxLength={80}
                    name="email"
                    required
                    size={20}
                    type="email"
                    value={formData.email}
                    onChange={onFormInputChange}
                  />
                </div>

                <div className={cx("submit-wrapper")}>
                  <p className={cx("required-message", "text-m")}>{labels?.newsletterFormGlobal?.required}</p>
                  {requestStatus.success === false && (
                    <h5 className={cx("text-m", "error-message")}>
                      {requestStatus.message}
                    </h5>
                  )}
                  <Button className={cx("submit")} label={labels?.newsletterFormGlobal?.submit} />
                </div>
              </form>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}

WifiForm.fragments = {
  entry: gql`
    fragment WifiForm on FlexiblePageContentContentWifiFormLayout {
      headline
      subhead
      body
      form {
        firstName
        lastName
        email
        required
        error
        success {
          headline
          subhead
        }
      }
    }
  `,
};
